/*  
<summary>
  Functional Component: ReservationInformation
  Developer: Pratiksha Sharma
  Created Date: 03-April-2025
  Description: To manage and display reservation information in a tabular format along with drag and drop functionality. This component allows users to view and manage reservations for different staff members over a daily or weekly view.
</summary>
<returns>JSX for the Reservation Management interface</returns>
*/

// Importing observer from mobx-react for state management
import { observer } from "mobx-react";
// Importing moment for date and time manipulation
import moment from "moment";
// Importing React and its hooks for component creation and state management
import React, {
  ChangeEvent,
  ChangeEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
// Importing useDrop from react-dnd for drag-and-drop functionality
import { useDrop } from "react-dnd";
// Importing toast for displaying notifications
import toast from "react-hot-toast";
// Importing FormattedMessage for internationalization
import { FormattedMessage } from "react-intl";
// Importing useHistory for navigation
import { useHistory, useLocation } from "react-router-dom";
// Importing SlidingPanel for sliding panel UI
import SlidingPanel from "react-sliding-side-panel";
// Importing utility functions for various operations
import {
  formatMinutesToHours,
  generateWeeklyHours,
  getDailyBusinessHoursMinMaxTime,
  getDailyBusinessHoursMinMaxTimeOrignal,
  getDailyBusinessHoursMinMaxTimeWeekly,
  getOpenTimeFromReservationSetting,
  getOpenTimeFromReservationSettingForCustomerSide,
  isCurrentDateTimeGreater,
} from "../../../constants/constants";
// Importing useStore for accessing global store
import { useStore } from "../../../contexts/store-provider";
// Importing helper function for local storage operations
import {
  getHeadquarterIdFromLocalStorage,
  getStoreIdFromLocalStorage,
  getUserTypeFromLocalStorage,
} from "../../../helpers/localstorage-helper";
// Importing a default icon for staff images
import demoStoreIcon from "../../../assets/Images/man.png";
// Importing FormLoader for loading spinner
import FormLoader from "../../../shared-components/FormLoader/FormLoader";
// Importing LanguageSlider for language selection
import LanguageSlider from "../../../shared-components/language-slider/language-slider";
// Importing route constants for navigation
import RoutesConstants from "../../../shared-components/routes/routes-constants";
// Importing formatMessage for message formatting
import { formatMessage } from "../../../translations/format-message";
// Importing AddReservationFormComponent for reservation form
import AddReservationFormComponent from "./form-component/add-reservation-form-component";
// Importing HOCs for various popup functionalities
import AddBreakDetailsPopupHOC from "./hoc/add-break-details-popup-hoc";
import AllMoreEventsPopupHOC from "./hoc/all-more-events-popup-hoc";
import AssignShiftPopupHOC from "./hoc/assign-shift-popup-hoc";
import ReservationDetailsPopupHOC from "./hoc/reservation-details-popup-hoc";
// Importing modal component for assigning shifts
import { ActionType } from "../../../constants/enums/action-type-enum";
import EventTypeEnum from "../../../constants/enums/event-type-enum";
import { ModuleType } from "../../../constants/enums/module-type-enum";
import userTypeEnum from "../../../constants/enums/user-type-enum";
import { isOperationPermittedRoleBase } from "../../../helpers/action-permission-helper";
import CustomDateInput from "../../../shared-components/input/custom-date-input";
import DropdownInput from "../../../shared-components/input/DropdownInput";
import NoAgreement from "../../../shared-components/no-data-svg/no-agreement-svg";
import DraggableEvent from "./draggableEvent";
import ShowAllReservationsPopupHOC from "./hoc/show-all-reservations-popup-hoc";
import AssignShiftStaffComponent from "./modal-components/assign-shift-staff-component";
import ShowAllReservationsPopup from "./modal-components/show-all-reservations-popup";
import DailySchedulerComponent from "./reservation-components/daily-scheduler-component";
import OuterDraggableCSSProperties from "./reservation-components/outer-draggable-css-properties";
import OuterDroppableComponent from "./reservation-components/outer-droppable-component";
import WeeklySchedulerComponent from "./reservation-components/weekly-scheduler-component";
import "./reservation-information.css";

const ReservationInformation: React.FC = (props: any) => {
  const history = useHistory();
  const {
    reservationSchedulerStore,
    staffManagementStore,
    masterManagementStore,
    bookingManagementStore,
    shiftManagementStore,
    customerManagementStore,
  } = useStore();
  const {
    getBreaks,
    getStaffBreaks,
    events,
    getStaffAppointments,
    getStaffAppointmentsState,
    GetStaffAppointmentsListService,
    updateDraggableAppointmentState,
    resetUpdateAppointmentState,
    updateDraggableBreakState,
    resetUpdateStaffBreakState,
    resetAddStaffBreakState,
    deleteStaffBreakByIdState,
    resetDeleteStaffBreakState,
    addStaffBreakState,
    resetGetStaffBreakByIdState,
    getStaffAppointmentsNonStaff,
    removeEvent,
    addDroppedEvent,
    removeDroppedEvent,
    addEventBackToOrignal,
    droppedEvents,
    resetDroppedEvents,
    updateReservationCancelState,
    resetUpdateReservationCancelState,
    updateBookingTypeState,
    resetBookingTypeState,
    updateReservationMemoState,
    resetUpdateReservationMemoState,
    getStaffAppointmentCountByDateRange,
    GetStaffAppointmentsCSVListService,
  } = reservationSchedulerStore;
  const { staffState } =
    staffManagementStore;
  const { addUpdateCustomerState, resetAddUpdateCustomerState } =
    customerManagementStore;
  const {
    addUpdateAppointmentState,
    resetBookingStore,
    resetAppointmentState,
  } = bookingManagementStore;
  const {
    GetReservationSettingByIdService,
    getReservationSettingDetail,
    GetAllStaffOptionServiceByStoreId,
    GetAllStaffOptionServiceByReservationDateStoreId,
    staffOptionsState,
    getAllStaffOptions,
    GetReservationSettingMinMaxTime,
    getReservationSettingMinMaxDetail,
    GetAllStoreOptionService,
    getAllStoreOptions,
    GetAllTimeService,
    storeOptionsState,
    GetConsumptionTaxByHeadquarterIdService,
    resetGetByIdReservationSettingState,
  } = masterManagementStore;
  // Constants
  const VIEW_DAILY = "daily";
  const VIEW_WEEKLY = "weekly";
  const DRAG_EVENT_TYPE = "EVENT";
  const {
    GetShiftScheduleListByStoreIdDateReservationService, // Service to get shift schedule list
    getShiftScheduleList, // Shift schedule list
  } = shiftManagementStore;
  const location = useLocation<{
    selectedDate?: string;
    selectedStoreId?: number;
  }>();
  const selectedDate = location.state?.selectedDate;
  const selectedStoreId = location.state?.selectedStoreId;
  const [view, setView] = useState("daily"); // State to manage the current view (daily/weekly)
  const [minuteInterval, setMinuteInterval] = useState(15); // State to manage the interval between minutes
  const [currentDate, setCurrentDate] = useState(
    selectedDate !== undefined
      ? new Date(selectedDate).toISOString()
      : new Date().toISOString()
  ); // State to manage the current date
  const [hoursShown, setHoursShown] = useState(6); // State to manage the number of hours shown in the table
  const [openPanel, setOpenPanel] = useState(false); // State to manage the visibility of the sliding panel
  const [selectedStaff, setSelectedStaff] = useState<number>(-1); // State to manage the selected staff ID
  const [selectedStaffName, setSelectedStaffName] = useState<any>(""); // State to manage the selected staff ID
  const [staffId, setStaffId] = useState<number>(-1); // State to manage the staff ID for assigning shifts
  const [selectedRow, setSelectedRow] = useState<any>({}); // State to manage the selected row data
  const [startDate, setStartDate] = useState(currentDate);
  const [staffName, setStaffName] = useState("");
  const userTypefromLocalStorage = getUserTypeFromLocalStorage();

  const { formattedCurrentDate, formattedStartOfWeek, formattedEndOfWeek } =
  useMemo(() => {
    const current = moment(currentDate); // parse current date

    // Week starts on Sunday
    const startOfWeek = current.clone().startOf("week");
    const endOfWeek = startOfWeek.clone().add(6, "days");

    return {
      formattedCurrentDate: current.format("YYYY-MM-DD"),
      formattedStartOfWeek: startOfWeek.format("YYYY-MM-DD"),
      formattedEndOfWeek: endOfWeek.format("YYYY-MM-DD"),
    };
  }, [currentDate]);

  const [storeIdSearch, setStoreIdSearch] = useState<number>(
    selectedStoreId != undefined
      ? selectedStoreId
      : getUserTypeFromLocalStorage() === userTypeEnum[userTypeEnum.Store]
      ? getStoreIdFromLocalStorage()
      : -1
  ); // State to manage the store ID for searching
  const weeklyHours = generateWeeklyHours(
    getDailyBusinessHoursMinMaxTimeWeekly(
      getReservationSettingMinMaxDetail,
      currentDate
    )
  ).length;
  const openTime = useMemo(() => {
    const hoursDetail = getDailyBusinessHoursMinMaxTimeWeekly(
      getReservationSettingMinMaxDetail,
      currentDate
    );
    return getOpenTimeFromReservationSetting(hoursDetail);
  }, [getReservationSettingMinMaxDetail, currentDate]);

  const hours = [...Array(weeklyHours)].map((_, i) =>
    moment(openTime, "HH:mm").add(i, "hours").format("HH:mm")
  ); // Generate an array of hours based on the opening time

  const dailyBusinessMinMaxTime = getDailyBusinessHoursMinMaxTimeOrignal(
    getReservationSettingDetail,
    currentDate
  );
  const dailyHours = generateWeeklyHours(
    getDailyBusinessHoursMinMaxTime(getReservationSettingDetail, currentDate)
  );
  const dailyOpenTime = getOpenTimeFromReservationSettingForCustomerSide(
    getDailyBusinessHoursMinMaxTime(getReservationSettingDetail, currentDate)
  ); // Get the opening time from reservation settings

  const minuteSteps = [5, 10, 15, 20, 30]; // Array of possible minute intervals
  const minutes = [...Array(60 / minuteInterval)].map(
    (_, i) => `:${i * minuteInterval}`
  ); // Generate an array of minute intervals
  const minutesTd = [...Array(60 / 5)].map((_, i) => `:${i * 5}`); // Generate an array of 5-minute intervals
  const tableRef = useRef<HTMLTableElement | null>(null); // Ref to access the table element
  const [tableWidth, setTableWidth] = useState(900); // State to manage the table width, default to minimum width
  const fixedColumnWidth = 200; // Fixed width for the staff column
  const timeColumnsWidth = useMemo(
    () => tableWidth - fixedColumnWidth,
    [tableWidth] // recompute only when tableWidth changes
  );

  const totalMinuteColumns = useMemo(
    () => hoursShown * minutesTd.length,
    [hoursShown, minutesTd.length]
  );
  const hourColumnWidthPx = useMemo(
    () => timeColumnsWidth / hoursShown,
    [timeColumnsWidth, hoursShown]
  );
  const tableCellWidth = useMemo(
    () => hourColumnWidthPx / 12,
    [hourColumnWidthPx]
  );
  const minuteColumnWidthPx = useMemo(
    () => timeColumnsWidth / totalMinuteColumns,
    [timeColumnsWidth, totalMinuteColumns]
  );
  const CELL_WIDTH = tableCellWidth; // Set the cell width
  const DEFAULT_TOP_MARGIN = 102; // Default top margin for events
  const TOTAL_ALLOWED_EVENTS_IN_ROW = 2; // Maximum number of events allowed in a row

  const openReservationWindow = (params: any) => {
    // Open the reservation window with the selected staff and time
    const selectedStaff = getAllStaffOptions?.find(
      (staff) => staff.id == params.StaffId
    );
    setSelectedRow({
      Time: params,
      SelectedStaff: selectedStaff,
      Date: params.Date,
    });
    setOpenPanel(true);
  };

  const closePanelhandler = () => {
    // Close the sliding panel
    setOpenPanel(false);
  };

  const handleViewChange = (date: string) => {
    // Change the current view (daily/weekly)
    setView("daily");
    setCurrentDate(date); // Reset current date to today
    setSelectedStaff(-1); // Reset selected staff
  }; // Change the current view (daily/weekly)

  // Fetch staff appointments list for the current date
  const callGetStaffAppointmentsList = useCallback(() => {
    if (!storeIdSearch || storeIdSearch < 0) return;
    
    const rangeStart =
    view === VIEW_DAILY ? formattedCurrentDate : formattedStartOfWeek;
    const rangeEnd = view === VIEW_DAILY ? rangeStart : formattedEndOfWeek;

    GetStaffAppointmentsListService(
      selectedStaff,
      storeIdSearch,
      rangeStart,
      rangeEnd
    );
    GetAllStaffOptionServiceByReservationDateStoreId(
      storeIdSearch,
      rangeStart,
      rangeEnd
    );
    GetShiftScheduleListByStoreIdDateReservationService(
      storeIdSearch,
      rangeStart,
      rangeEnd
    );
  }, [
    storeIdSearch,
    view,
    selectedStaff,
    formattedCurrentDate,
    formattedStartOfWeek,
    formattedEndOfWeek,
  ]);

  const callCSVOutput = () => {
    const start = moment(currentDate)
      .clone()
      .startOf("week")
      ?.format("YYYY-MM-DD");
    const end = moment(start)?.add(6, "days")?.format("YYYY-MM-DD");
    if (view == "daily") {
      GetStaffAppointmentsCSVListService(
        selectedStaff,
        storeIdSearch,
        view == "daily" ? moment(currentDate).format("YYYY-MM-DD") : startDate,
        ""
      );
    } else {
      GetStaffAppointmentsCSVListService(
        selectedStaff,
        storeIdSearch,
        start,
        end
      );
    }
  };

  useEffect(() => {
    resetDroppedEvents();
  }, []);

  useEffect(() => {
    // Fetch reservation settings and staff options on component mount
    if (storeIdSearch > -1) {
      const HeadquaterId = getHeadquarterIdFromLocalStorage();
      GetAllTimeService();
      GetReservationSettingMinMaxTime(storeIdSearch);
      GetReservationSettingByIdService(storeIdSearch);
      GetAllStaffOptionServiceByStoreId(storeIdSearch);
      if (HeadquaterId > 0)
        GetConsumptionTaxByHeadquarterIdService(
          getHeadquarterIdFromLocalStorage()
        );
    }
    GetAllStoreOptionService();
    // Store the current date in local storage
    localStorage.setItem(
      "currentDate",
      moment(currentDate).format("YYYY-MM-DD")
    );
  }, [storeIdSearch]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateCustomerSuccess" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateCustomerState.success) {
      callGetStaffAppointmentsList();
      resetAddUpdateCustomerState();
    }
  }, [addUpdateCustomerState.success]);

  useEffect(() => {
    const formattedDate = moment(currentDate).format("YYYY-MM-DD");
    localStorage.setItem("currentDate", formattedDate);

    callGetStaffAppointmentsList();
  }, [view, minuteInterval, currentDate, selectedStaff, storeIdSearch]);

  const handlePrev = () => {
    setCurrentDate((prev) => {
      const prevDate = moment(prev)
        .subtract(view === "daily" ? 1 : 7, "days")
        .format("YYYY-MM-DD");

      localStorage.setItem("currentDate", prevDate);

      return prevDate;
    });
  };

  // Function to navigate to the next day or week based on the current view
  const handleNext = () => {
    setCurrentDate((prev) => {
      const nextDate = moment(prev)
        .add(view === "daily" ? 1 : 7, "days")
        .format("YYYY-MM-DD");

      localStorage.setItem("currentDate", nextDate);

      return nextDate;
    });
  };

  // Adjust the number of hours shown in the table within the range of 4 to 8
  const handleHoursChange = (change: number) => {
    setHoursShown((prev) => Math.min(8, Math.max(4, prev + change)));
  };

  // Get the date range for the current week
  const getWeekDates = () => {
    const start = moment(currentDate).startOf("week");
    const end = moment(start).add(6, "days");
    return `${start.format("D")} - ${end.format("D")}`;
  };

  // Get the formatted current date with day abbreviation
  const getCurrentDate = () => {
    const dayKey = moment(currentDate).format("ddd");
    return (
      <>
        {moment(currentDate).format("DD")}
        <br />
        <small>{formatMessage(dayKey)}</small>
      </>
    );
  };

  useEffect(() => {
    if (updateReservationMemoState.success) {
      callGetStaffAppointmentsList();
      resetUpdateReservationMemoState(); // Reset memo state
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateReservationMemoState.success]);

  useEffect(() => {
    if (updateBookingTypeState.success) {
      callGetStaffAppointmentsList();
      resetBookingTypeState(); // Reset booking type state
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateBookingTypeState.success]);

  // Handle updates to draggable appointments
  useEffect(() => {
    if (updateDraggableAppointmentState.success) {
      callGetStaffAppointmentsList();
      resetUpdateAppointmentState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateDraggableAppointmentState.success]);

  useEffect(() => {
    if (updateDraggableAppointmentState.error) {
      callGetStaffAppointmentsList();
      resetUpdateAppointmentState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateDraggableAppointmentState.error]);

  // Handle updates to draggable breaks
  useEffect(() => {
    if (updateDraggableBreakState.success) {
      callGetStaffAppointmentsList();
      resetGetStaffBreakByIdState();
      resetUpdateStaffBreakState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateDraggableBreakState.success]);

  // Handle successful addition of staff breaks
  useEffect(() => {
    if (addStaffBreakState.success) {
      callGetStaffAppointmentsList();
      resetAddStaffBreakState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addStaffBreakState.success]);

  // Handle successful deletion of staff breaks
  useEffect(() => {
    if (deleteStaffBreakByIdState.success) {
      callGetStaffAppointmentsList();
      resetGetStaffBreakByIdState();
      resetDeleteStaffBreakState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteStaffBreakByIdState.success]);

  // Handle successful addition or update of appointments
  useEffect(() => {
    if (addUpdateAppointmentState.success) {
      toast.success(formatMessage("added_success"));
      setOpenPanel(false);
      callGetStaffAppointmentsList();
      resetBookingStore();
      resetAppointmentState();
    }
  }, [addUpdateAppointmentState.success]);

  // Handle errors during appointment addition or update
  useEffect(() => {
    if (addUpdateAppointmentState.error) {
      // toast.error(formatMessage(addUpdateAppointmentState.error));
      resetBookingStore();
      setOpenPanel(false);
      resetAppointmentState();
      callGetStaffAppointmentsList();
    }
  }, [addUpdateAppointmentState.error]);

  useEffect(() => {
    if (updateReservationCancelState.success) {
      toast.success(formatMessage("updated_success"));
      callGetStaffAppointmentsList();
      resetUpdateReservationCancelState();
    }
  }, [updateReservationCancelState.success]);

  // Update the table width dynamically based on the container size
  useEffect(() => {
    const updateTableWidth = () => {
      if (tableRef.current) {
        const actualWidth = tableRef.current.getBoundingClientRect().width;
        const finalWidth = Math.max(actualWidth, 900);
        setTableWidth(finalWidth);
      }
    };

    updateTableWidth(); // Measure on mount
    window.addEventListener("resize", updateTableWidth); // Re-measure on resize
    return () => window.removeEventListener("resize", updateTableWidth);
  }, [getStaffAppointments]);

  // Navigate to the staff detail page
  const openStaffDetail = (Id: number) => {
    history.push({
      pathname: RoutesConstants.StoreStaffInformation,
      state: { staffId: Id },
    });
  };

  const closeAddReservation = () => {
    resetBookingStore();
    setOpenPanel(false);
    resetAppointmentState();
  };

  const days =
    view === "daily"
      ? getAllStaffOptions
          .filter((item) =>
            selectedStaff === -1 ? true : item.id === selectedStaff
          )
          .map((staff, i) => {
            const staffName = staff.data?.StaffName;
            const staffImage = staff.data?.Image ?? demoStoreIcon;
            const staffDate = moment(currentDate).format("YYYY-MM-DD");

            return (
              <div
                key={staff.id ?? i}
                data-action={staff.id}
                data-id={staffName}
                data-index={staffDate}
                data-role={view}
                className="d-flex align-items-center"
              >
                <img
                  src={staffImage}
                  alt={staffName}
                  className="staffImg m-1"
                />
                <div className="staffInfo">
                  <p className="m-0">{staffName}</p>
                  <small className="text-muted">
                    <FormattedMessage id="reservations" />:{" "}
                    {staff.data?.TotalReservations}
                  </small>
                  <br />
                  <small className="text-muted">
                    <FormattedMessage id="booking_time" />:{" "}
                    {formatMinutesToHours(staff.data?.TotalBookingTime)}
                  </small>
                </div>
                <div className="dropdown staffAction">
                  <a
                    href="#"
                    role="button"
                    id="actionDropdown"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <i className="bi bi-three-dots-vertical fs-5"></i>
                  </a>
                  <ul
                    className="dropdown-menu"
                    aria-labelledby="actionDropdown"
                  >
                    <li>
                      <a
                        className="dropdown-item"
                        onClick={() => openStaffDetail(staff?.data?.Id)}
                      >
                        <i className="bi bi-person me-2"></i>{" "}
                        <FormattedMessage id="details" />
                      </a>
                    </li>
                    {isOperationPermittedRoleBase(
                      ModuleType.ReservationManagement,
                      ActionType.Add
                    ) && (
                      <li>
                        <a
                          className="dropdown-item"
                          onClick={() =>
                            openAssignShiftHandler({ Id: staff?.id })
                          }
                        >
                          <i className="bi bi-calendar3 me-2"></i>{" "}
                          <FormattedMessage id="set_shift" />
                        </a>
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            );
          })
      : (() => {
          const start = moment(currentDate)
            .startOf("week")
            .format("YYYY-MM-DD");
          const end = moment(start).clone().add(6, "days").format("YYYY-MM-DD");

          return [...Array(7)].map((_, i) => {
            const date = moment(start).clone().add(i, "days");
            const dateStr = date.format("YYYY-MM-DD");
            const dateNum = date.format("D");
            const dateShortName = date.format("ddd");

            const staffData = getStaffAppointmentCountByDateRange(
              selectedStaff,
              start,
              end
            )?.find((item) => item.date === dateStr);

            return (
              <div
                className="text-center"
                key={dateStr}
                data-action={selectedStaff}
                data-id={selectedStaffName}
                data-index={dateStr}
                data-role={view}
                onClick={() => {
                  setStaffName(selectedStaffName);
                  setStartDate(dateStr);
                  props.showAllReservationsToggleHandler();
                }}
              >
                <div className="text-lg font-medium">{dateNum}</div>
                <small className="text-muted">
                  {formatMessage(dateShortName)}
                </small>
                <div className="staffInfo mw-100">
                  <small className="text-muted">
                    <FormattedMessage id="reservations" />:{" "}
                    {staffData?.count ?? 0}
                  </small>
                  <br />
                  <small className="text-muted">
                    <FormattedMessage id="booking_time" />:{" "}
                    {formatMinutesToHours(staffData?.totalMinutes)}
                  </small>
                </div>
              </div>
            );
          });
        })();

  //Handling the Staff Change
  const handleStaffSelect = (staffId: number, staffName: string) => {
    setSelectedStaff(staffId); // toggle
    setSelectedStaffName(staffName);
  };

  // Set up a drop target for drag-and-drop functionality
  const [{ isOver }, drop] = useDrop(() => ({
    accept: "EVENT",
    drop: (item: { day: string; time: string }) => {},
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  }));

  // Open the assign shift popup with the selected staff ID
  const openAssignShiftHandler = (row: { Id: number }) => {
    setStaffId(row.Id);
    props.assignShiftToggleHandler();
  };

  const handleStoreIdSearch: ChangeEventHandler<HTMLSelectElement> =
    useCallback((event: ChangeEvent<HTMLSelectElement>) => {
      resetGetByIdReservationSettingState();
      setStoreIdSearch(event.target.value as unknown as number);
    }, []);

  const initialValuesReservation: any = {
    selectedServices: [],
    selectedCustomer: "-1",
    IsNewCustomer: false,
    LastNameKana: "",
    FirstNameKana: "",
    Email: "",
    Phone: "",
    Gender: "2",
  };

  return (
    <>
      {(getStaffAppointmentsState.inProgress ||
        staffState.inProgress ||
        storeOptionsState.inProgress ||
        staffOptionsState.inProgress ||
        updateDraggableBreakState.inProgress ||
        addStaffBreakState.inProgress ||
        deleteStaffBreakByIdState.inProgress ||
        updateReservationCancelState.inProgress ||
        addUpdateAppointmentState.inProgress ||
        updateBookingTypeState.inProgress ||
        addUpdateCustomerState.inProgress ||
        updateDraggableAppointmentState.inProgress) && (
        <FormLoader
          loading={
            getStaffAppointmentsState.inProgress ||
            staffState.inProgress ||
            storeOptionsState.inProgress ||
            updateDraggableBreakState.inProgress ||
            staffOptionsState.inProgress ||
            addStaffBreakState.inProgress ||
            deleteStaffBreakByIdState.inProgress ||
            updateDraggableAppointmentState.inProgress ||
            updateReservationCancelState.inProgress ||
            updateBookingTypeState.inProgress ||
            addUpdateCustomerState.inProgress ||
            addUpdateAppointmentState.inProgress
          }
        />
      )}

      {props.assignShiftPopup && (
        <AssignShiftStaffComponent
          id={staffId}
          staffId={staffId}
          staffList={getAllStaffOptions}
          onModalClosed={() => {
            props.assignShiftToggleHandler();
            callGetStaffAppointmentsList();
          }}
          storeId={storeIdSearch}
        />
      )}

      {props.showAllReservationsPopup && (
        <ShowAllReservationsPopup
          title={moment(startDate).format("YYYY-MM-DD")}
          onModalClosed={() => {
            props.showAllReservationsToggleHandler();
          }}
          selectedStaff={selectedStaff}
          allStaffs={getAllStaffOptions}
          storeIdSearch={storeIdSearch}
          isLoading={getStaffAppointmentsState.inProgress}
        />
      )}

      {/* Top navigation bar */}
      <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
        <div className="row align-items-center">
          <div className="col-6">
            <div className="page_title_wrap">
              <h1 className="page_title">
                <FormattedMessage id="reservation_management" />
              </h1>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <FormattedMessage id="reservation_management" />
                </li>
              </ol>
            </div>
          </div>
          <div className="col-6">
            <ul className="add-list">
              <li>
                <LanguageSlider />
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div
        className="container-fluid mt-4 p-0"
        data-minute-interval={minuteInterval}
      >
        {/* top bar containing calendar and download button and drop area */}
        <div className="row g-2">
          {(getUserTypeFromLocalStorage() ===
            userTypeEnum[userTypeEnum.Store]) ==
            false && (
            <React.Fragment>
              <div className="col-xxl-2 col-xl-3 col-lg-2 col-md-6 col-sm-6 col-12">
                <DropdownInput
                  className="form-control"
                  placeholder={formatMessage("search_by_store_name")}
                  value={storeIdSearch}
                  onChange={handleStoreIdSearch}
                  options={getAllStoreOptions.map((option) => ({
                    label: option.value,
                    value: option.id,
                  }))}
                />
              </div>
            </React.Fragment>
          )}

          <React.Fragment>
            <div className="col-xxl-2 col-xl-3 col-lg-3 col-md-6 col-sm-6 col-12">
              <CustomDateInput
                className="form-control"
                value={moment(currentDate).format("YYYY-MM-DD")}
                onChange={(value: string) => {
                  setCurrentDate(value);
                  localStorage.setItem("currentDate", value);
                }}
              />
            </div>
            {storeIdSearch > 0 &&
              userTypefromLocalStorage !==
                userTypeEnum[userTypeEnum.SuperAdmin] && (
                <div className="col-xxl-2 col-lg-2 col-md-6 col-sm-6 col-12 order-lg-3 text-end">
                  <button
                    className={`btn btn-primary h-100 w-100 `}
                    onClick={() => {
                      callCSVOutput();
                    }}
                    // disabled={!(getStaffAppointments.length > 0)}
                  >
                    <i className="bi bi-download me-1"></i>
                    <FormattedMessage id="csv_output" />
                  </button>
                </div>
              )}

            {(getUserTypeFromLocalStorage() ===
              userTypeEnum[userTypeEnum.Store]) ==
              true && (
              <>
                {getReservationSettingDetail?.StoreId > 0 && (
                  <div className="col-xxl-8 col-xl-6 col-lg-7 col-md-12 col-sm-12 col-12 order-lg-2">
                    <div className="outside-drop-area position-relative">
                      {isOperationPermittedRoleBase(
                        ModuleType.ReservationManagement,
                        ActionType.Add
                      ) && (
                        <OuterDroppableComponent
                          time=""
                          currentDate={localStorage.getItem(currentDate)!}
                          onDropEvent={(toTime, data, currentDate) => {
                            const eventDate = moment(data?.Date).format(
                              "YYYY-MM-DD"
                            );

                            if (
                              isCurrentDateTimeGreater(
                                eventDate,
                                data?.StartTime
                              )
                            ) {
                              toast.error(
                                formatMessage("cannot_drag_past_events")
                              );
                              return;
                            }

                            if (data?.EventType === EventTypeEnum?.StaffBreak) {
                              toast.error(
                                formatMessage("you_cant_drop_break_here")
                              );
                              return;
                            }

                            addDroppedEvent(data);
                            removeEvent(data?.Id);
                          }}
                        >
                          {droppedEvents?.length > 0 ? (
                            <div className="event-box-wrapper">
                              {droppedEvents.map(
                                (eventDropped: any, index: number) => (
                                  <DraggableEvent
                                    key={eventDropped?.Id ?? index}
                                    area="Outer"
                                    currentDate={currentDate}
                                    data={eventDropped}
                                    style={OuterDraggableCSSProperties(
                                      1,
                                      2,
                                      0,
                                      86
                                    )}
                                  >
                                    {() => (
                                      <div className="event upcoming-event position-relative">
                                        <div>
                                          {eventDropped?.CustomerName}
                                          <br />
                                          {eventDropped?.StartTime} ~{" "}
                                          {eventDropped?.EndTime}
                                        </div>

                                        <div
                                          style={{
                                            position: "absolute",
                                            top: "2px",
                                            right: "0px",
                                          }}
                                        >
                                          <button
                                            className="Crossicons"
                                            onClick={() => {
                                              removeDroppedEvent(
                                                eventDropped?.Id
                                              );
                                              addEventBackToOrignal(
                                                eventDropped
                                              );
                                            }}
                                          >
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              width="16"
                                              height="16"
                                              fill="#fff"
                                              className="bi bi-x-circle-fill"
                                              viewBox="0 0 16 16"
                                            >
                                              <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0M5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293z" />
                                            </svg>
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  </DraggableEvent>
                                )
                              )}
                            </div>
                          ) : (
                            <FormattedMessage id="outer_draggable" />
                          )}
                        </OuterDroppableComponent>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </React.Fragment>
        </div>

        {/* main table containing the reservation information */}
        {/* This table will be generated dynamically based on the selected view (daily/weekly) */}
        <div className="card mb-3 mt-3">
          {getReservationSettingDetail.StoreId > 0 &&
          getReservationSettingDetail.SeatAvailable > 0 ? (
            <div className="card-body reservation-table  no-select">
              {/* Toolbar row containing view type, date navigation, sync & settings */}
              <div className="row g-1 g-lg-3 justify-content-between align-items-center mb-3">
                {/* View type and staff selector */}
                <div className="col-xl-3 order-1 col-sm-6 col-12">
                  <div className="row g-2 align-items-center">
                    <div className="col-4">
                      <select
                        value={view}
                        className="form-select"
                        disabled={droppedEvents?.length > 0}
                        onChange={(e) => {
                          setView(e.target.value);
                          setSelectedStaff(-1);
                          setSelectedStaffName("AllStaffs");
                        }}
                      >
                        <option value="daily">
                          <FormattedMessage id="daily" />
                        </option>
                        <option value="weekly">
                          <FormattedMessage id="weekly" />
                        </option>
                      </select>
                    </div>

                    {view === "daily" && (
                      <div className="col-8">
                        <select
                          className="form-select"
                          onChange={(e) =>
                            setSelectedStaff(Number(e.target.value))
                          }
                        >
                          <option value="-1" key={-1} id="-1">
                            <FormattedMessage id="all_staff" />
                          </option>
                          {getAllStaffOptions?.map((staff, index) => (
                            <option
                              value={staff?.id}
                              key={staff?.id ?? index}
                              id={`staff-${staff?.id}`}
                            >
                              {staff.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>

                {/* Date navigation */}
                <div className="col-xl-5 col-sm-12 order-3 order-xl-2 col-12 text-center">
                  <div className="d-flex justify-content-center align-items-center">
                    <button className="btn btn-light me-2" onClick={handlePrev}>
                      <i className="bi bi-chevron-left"></i>
                    </button>
                    <p className="mb-0 dateValue">
                      {view === "daily" ? getCurrentDate() : getWeekDates()}
                    </p>
                    <button className="btn btn-light ms-2" onClick={handleNext}>
                      <i className="bi bi-chevron-right"></i>
                    </button>
                  </div>
                </div>

                {/* Sync, hours, minute interval */}
                <div className="col-xl-4 col-sm-6 order-2 order-xl-3 col-12">
                  <div className="d-flex justify-content-end align-items-center gap-2">
                    <button
                      className="me-2"
                      onClick={callGetStaffAppointmentsList}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="22"
                        height="22"
                        fill="currentColor"
                        className="bi bi-arrow-repeat"
                        viewBox="0 0 16 16"
                      >
                        <title>{formatMessage("sync")}</title>
                        <path d="M11.534 7h3.932a.25.25 0 0 1 .192.41l-1.966 2.36a.25.25 0 0 1-.384 0l-1.966-2.36a.25.25 0 0 1 .192-.41m-11 2h3.932a.25.25 0 0 0 .192-.41L2.692 6.23a.25.25 0 0 0-.384 0L.342 8.59A.25.25 0 0 0 .534 9" />
                        <path
                          fillRule="evenodd"
                          d="M8 3c-1.552 0-2.94.707-3.857 1.818a.5.5 0 1 1-.771-.636A6.002 6.002 0 0 1 13.917 7H12.9A5 5 0 0 0 8 3M3.1 9a5.002 5.002 0 0 0 8.757 2.182.5.5 0 1 1 .771.636A6.002 6.002 0 0 1 2.083 9z"
                        />
                      </svg>
                    </button>

                    <div className="d-flex align-items-center">
                      <button
                        className="btn btn-dark btn-sm rounded-circle me-2"
                        onClick={() => handleHoursChange(-1)}
                      >
                        <i className="bi bi-dash-lg"></i>
                      </button>
                      <h5 className="mb-0">
                        {hoursShown}
                        <FormattedMessage id="h" />
                      </h5>
                      <button
                        className="btn btn-dark btn-sm rounded-circle ms-2"
                        onClick={() => handleHoursChange(1)}
                      >
                        <i className="bi bi-plus-lg"></i>
                      </button>
                    </div>

                    <select
                      className="form-select w-25"
                      value={minuteInterval}
                      onChange={(e) =>
                        setMinuteInterval(Number(e.target.value))
                      }
                    >
                      {minuteSteps.map((step) => (
                        <option key={step} value={step}>
                          {step} <FormattedMessage id="min" />
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Scheduler components */}
              {view === "daily" ? (
                <DailySchedulerComponent
                  minutes={minutes}
                  hoursShown={hoursShown}
                  hours={dailyHours}
                  minuteInterval={minuteInterval}
                  DEFAULT_TOP_MARGIN={DEFAULT_TOP_MARGIN}
                  TOTAL_ALLOWED_EVENTS_IN_ROW={TOTAL_ALLOWED_EVENTS_IN_ROW}
                  CELL_WIDTH={CELL_WIDTH}
                  currentDate={currentDate}
                  view={view}
                  getAllStaffOptions={getAllStaffOptions}
                  handleStaffSelect={handleStaffSelect}
                  selectedStaff={selectedStaff}
                  openTime={dailyOpenTime}
                  minutesTd={minutesTd}
                  getStaffAppointments={getStaffAppointments}
                  getBreaks={getBreaks}
                  openReservationWindow={openReservationWindow}
                  droppedEvents={droppedEvents}
                  days={days}
                  hourColumnWidthPx={hourColumnWidthPx}
                  minuteColumnWidthPx={minuteColumnWidthPx}
                  tableRef={tableRef}
                  getReservationSettingDetail={getReservationSettingDetail}
                  storeId={storeIdSearch}
                  dailyBusinessMinMaxTime={dailyBusinessMinMaxTime}
                  reservationSettingMinMaxDetail={
                    getReservationSettingMinMaxDetail
                  }
                  viewOnly={
                    !isOperationPermittedRoleBase(
                      ModuleType.ReservationManagement,
                      ActionType.Add
                    )
                  }
                  shiftScheduleList={getShiftScheduleList}
                />
              ) : (
                <WeeklySchedulerComponent
                  minutes={minutes}
                  hoursShown={hoursShown}
                  hours={hours}
                  minuteInterval={minuteInterval}
                  DEFAULT_TOP_MARGIN={DEFAULT_TOP_MARGIN}
                  TOTAL_ALLOWED_EVENTS_IN_ROW={TOTAL_ALLOWED_EVENTS_IN_ROW}
                  CELL_WIDTH={CELL_WIDTH}
                  currentDate={currentDate}
                  view={view}
                  getAllStaffOptions={getAllStaffOptions}
                  handleStaffSelect={handleStaffSelect}
                  selectedStaff={selectedStaff}
                  openTime={openTime}
                  minutesTd={minutesTd}
                  getStaffAppointmentsNonStaff={getStaffAppointmentsNonStaff}
                  getBreaks={getStaffBreaks}
                  openReservationWindow={openReservationWindow}
                  droppedEvents={droppedEvents}
                  days={days}
                  hourColumnWidthPx={hourColumnWidthPx}
                  minuteColumnWidthPx={minuteColumnWidthPx}
                  tableRef={tableRef}
                  getReservationSettingDetail={getReservationSettingDetail}
                  storeId={storeIdSearch}
                  viewOnly={
                    !isOperationPermittedRoleBase(
                      ModuleType.ReservationManagement,
                      ActionType.Add
                    )
                  }
                  changeView={handleViewChange}
                  shiftScheduleList={getShiftScheduleList}
                  reservationSettingMinMaxTime={getReservationSettingDetail}
                />
              )}
            </div>
          ) : (
            <div className="p-3 text-center">
              <NoAgreement />
              <h3>
                {storeIdSearch == -1 ? (
                  <FormattedMessage id="please_select_store" />
                ) : (
                  <FormattedMessage id="please_configure_reservation_settings" />
                )}
              </h3>
            </div>
          )}
        </div>
      </div>

      <SlidingPanel
        type={"right"}
        isOpen={openPanel}
        noBackdrop={false}
        size={100}
      >
        <div className="bg_slide panel">
          <div className="row bb p-2 pl-4 g-0 align-items-center ">
            <div className="col-10">
              <h2 className="fw-bold mb-0 mt-1 py-2">
                <FormattedMessage id="add_reservation" />
              </h2>
            </div>
            <div className="col-2">
              <span title={formatMessage("close")}>
                <svg
                  onClick={closePanelhandler}
                  role="button"
                  xlinkTitle="close"
                  xmlns="http://www.w3.org/2000/svg"
                  width="30"
                  height="30"
                  fill="currentColor"
                  className="bi bi-x ms-auto d-block"
                  viewBox="0 0 16 16"
                >
                  <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                </svg>
              </span>
            </div>
          </div>
          <div className="group_detailsWrapper">
            <AddReservationFormComponent
              initialValues={{
                ...initialValuesReservation,
                selectedStaff: selectedRow?.SelectedStaff?.id,
              }}
              staff={selectedRow?.SelectedStaff}
              time={selectedRow?.Time}
              date={selectedRow?.Date}
              reservationSettingDetail={getReservationSettingDetail}
              onModalClosed={closeAddReservation}
              storeId={storeIdSearch}
            />
          </div>
        </div>
      </SlidingPanel>
      {/* {JSON.stringify(getDailyBusinessHoursMinMaxTime(getReservationSettingDetail, currentDate))} */}
      {/* {JSON.stringify(getStaffAppointmentsNonStaff)} */}
    </>
  );
};

export default AssignShiftPopupHOC(
  AllMoreEventsPopupHOC(
    AddBreakDetailsPopupHOC(
      ReservationDetailsPopupHOC(
        ShowAllReservationsPopupHOC(observer(ReservationInformation))
      )
    )
  )
);
